import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, BookMarked, Send } from "lucide-react";
import { toast } from "sonner";
import { submitReference } from "@/lib/rexxponsApi";

export const SubmitReferenceModal = ({ open, onClose, docId, docKind }) => {
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [note, setNote] = useState("");
  const [submitter, setSubmitter] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const t = title.trim();
    const u = url.trim();
    if (!t) return toast.error("Please give the reference a title.");
    if (!/^https?:\/\//i.test(u)) return toast.error("Please provide a valid http(s) URL.");
    setBusy(true);
    try {
      await submitReference({ doc_id: docId, doc_kind: docKind, title: t, url: u, note: note.trim(), submitter: submitter.trim() });
      toast.success("Reference submitted for review. Thank you for contributing.");
      setTitle("");
      setUrl("");
      setNote("");
      setSubmitter("");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit that reference. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="reference-modal"
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="relative w-full max-w-lg bg-card border border-border rounded-2xl p-7 shadow-2xl"
            initial={{ y: 24, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 12, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid="reference-close-btn"
              onClick={onClose}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Close"
            >
              <X strokeWidth={1.5} className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3 mb-2">
              <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 border border-primary/30">
                <BookMarked strokeWidth={1.5} className="w-4 h-4 text-primary" />
              </span>
              <h2 className="font-heading text-2xl font-medium tracking-tight">Contribute a source</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
              Know a better reference for this topic? Submit it — approved sources are shown to everyone.
            </p>
            <form onSubmit={submit} className="space-y-4">
              <input
                data-testid="reference-title-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Reference title"
                className="w-full bg-secondary/60 border border-border rounded-lg px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary/50 transition-colors"
              />
              <input
                data-testid="reference-url-input"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://…"
                className="w-full bg-secondary/60 border border-border rounded-lg px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary/50 transition-colors"
              />
              <textarea
                data-testid="reference-note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Why is this a good source? (optional)"
                rows={3}
                maxLength={1000}
                className="w-full bg-secondary/60 border border-border rounded-lg px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary/50 transition-colors resize-none"
              />
              <input
                data-testid="reference-submitter-input"
                value={submitter}
                onChange={(e) => setSubmitter(e.target.value)}
                placeholder="Your name (optional)"
                className="w-full bg-secondary/60 border border-border rounded-lg px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary/50 transition-colors"
              />
              <button
                data-testid="reference-submit-btn"
                type="submit"
                disabled={busy}
                className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground font-medium text-sm tracking-[0.08em] uppercase rounded-lg px-4 py-3 hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                <Send strokeWidth={1.8} className="w-4 h-4" />
                {busy ? "Submitting…" : "Submit for review"}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
